import beta from '@/assets/icons/beta.svg';
import WavingHandIcon from '@/assets/icons/waving_hand.png';
import { Button, HSpacer, Text, Toolbar, VSpacer } from '@/components/DesignSystem';
import { BoxedImage } from "@/components/DesignSystem/BoxedImage/BoxedImage";
import { AxiomLogo } from '@/components/DesignSystem/Logo/AxiomLogo';
import {
  Filter,
  FilterOption,
  FilterSelections,
} from '@/components/DesignSystem/Toolbar/interfaces';
import { AboutInsightsModal } from '@/components/shared/AboutInsightsModal';
import { AppConfig } from "@/constants/AppConfig";
import { QueryKeys } from '@/constants/QueryKeys';
import { Routes } from '@/constants/Routes';
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { useProgramPeriodSelector } from '@/hooks/useProgramPeriodSelector';
import { CustomerChurnSection } from '@/pages/RetailerOverview/CustomerChurnSection';
import { KPICard } from '@/pages/RetailerOverview/PointsOverview/KPICard';
import { PointsOverviewCard } from '@/pages/RetailerOverview/PointsOverview/PointsOverviewCard';
import { ProgramPeriodSelector } from '@/pages/RetailerOverview/ProgramPeriodSelector';
import { TierProgressCard } from '@/pages/RetailerOverview/TierProgress/TierProgressCard';
import { ManufacturerApi } from '@/utilities/api/ManufacturerApi';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Insights from '@mui/icons-material/Insights';
import TableChartOutlined from '@mui/icons-material/TableChartOutlined';
import { Container, Divider, Grid, Stack } from "@mui/material";
import { ThemeProvider } from '@mui/material/styles';
import { ReportType } from '@shared/enums';
import { formatDateOnly, formatNumber, formatPercent, getAccountName } from '@shared/utilities';
import React, { useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { agronomyProgramsOnly } from '@/utilities/RewardsProgramUtility';
import { createRetailerTheme } from '@/themes/MUITheme/retailerTheme';

export const RetailerOverview = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isInternal, isRetailerAdmin, retailer: authRetailer, user } = useAuthentication();
  const [searchParams] = useSearchParams();
  // Internal admins have no retailer of their own, so they open this page from Reports with one.
  const retailerIdParam = searchParams.get('retailerId');
  const { data: selectedRetailer, isError: isRetailerMissing } = useQuery(
    [QueryKeys.GET_RETAILER_BY_ID, retailerIdParam],
    () => RetailerApi.get(retailerIdParam as string),
    { enabled: !authRetailer && !!retailerIdParam, retry: false },
  );
  const retailer = authRetailer ?? selectedRetailer;
  const retailerSearch = isInternal && retailer ? `?retailerId=${retailer.id}` : '';
  // A retailer's own users already get its theme app-wide; internal admins get it on this page.
  const brandTheme = useMemo(
    () => (isInternal && retailer?.brandColor ? createRetailerTheme(retailer.brandColor) : null),
    [isInternal, retailer?.brandColor],
  );
  const retailerLogoUrl = isInternal && retailer?.image
    ? `${AppConfig.staticImageHost}/${retailer.image}`
    : null;
  const [manufacturerFilterSelections, setManufacturerFilterSelections] =
    useState<FilterSelections | undefined>(() => new Map());
  const [showAboutInsightsModal, setShowAboutInsightsModal] = useState<boolean>(false);
  const hasMultipleRewardPrograms = agronomyProgramsOnly(retailer?.rewardsPrograms).length > 1;

  const {
    selectedProgramPeriod,
    setSelectedProgramPeriod,
  } = useProgramPeriodSelector({
    rewardsPrograms: retailer?.rewardsPrograms,
  });

  const selectedManufacturerId =
    manufacturerFilterSelections?.get('manufacturer')?.values().next().value;

  const campaignImageUrl = retailer?.campaignImage && (
    `${AppConfig.staticImageHost}/${retailer.campaignImage}`
  );

  const { data: kpis } = useQuery(
    [QueryKeys.GET_POINTS_KPIS, retailer?.id, selectedManufacturerId, selectedProgramPeriod],
    () => RetailerApi.getPointsKPIs(
      retailer!.id,
      {
        programId: selectedProgramPeriod?.id,
        ...(selectedManufacturerId ? { manufacturerId: selectedManufacturerId } : {}),
      },
    ),
    { enabled: !!retailer?.id },
  );

  const { data: manufacturers } = useQuery(
    [QueryKeys.GET_MANUFACTURERS, retailer?.id],
    () => ManufacturerApi.list({
      hasPromotions: true,
      retailerId: retailer!.id,
    }),
    { enabled: !!retailer?.id },
  );

  const Header = (
    <Grid
      bgcolor="primary.main"
      borderRadius="16px"
      color="primary.contrastText"
      container
      data-testid="overview-header"
      height="104px"
      padding="12px 24px"
    >
      <Grid alignContent="center" item padding="0px !important" sm={3} xs={12}>
        <Text category="display-small">
          {t($ => $.overview.title)}
        </Text>
      </Grid>
      <Grid alignContent="center" item padding="0px !important" sm={6} xs={12}>
        <Stack alignItems="center" direction="row" justifyContent="center">
          {campaignImageUrl && (
            <BoxedImage height="80px" src={campaignImageUrl} width="142px" />
          )}
        </Stack>
      </Grid>
      <Grid alignContent="center" item padding="0px !important" sm={3} xs={12}>
        <Stack alignItems="center" direction="row" justifyContent="end">
          <img alt="waving-hand-icon" height="21.667px" src={WavingHandIcon} width="21.667px" />
          <HSpacer size="3" />
          <Text category="body-xlarge">
            {t($ => $.overview.greeting, { name: user ? getAccountName(user) : '' })}
          </Text>
        </Stack>
      </Grid>
    </Grid>
  );

  const manufacturerFilter = useMemo(() => ([{
    id: 'manufacturer',
    isChipLabelVerbose: true,
    label: t($ => $.overview.manufacturer),
    options: (manufacturers
      ?.filter(({ name }) => !!name)
      .map(({ id, name }) => ({
        id,
        label: name,
      })) ?? []) as FilterOption[],
    selectionMethod: 'single-select',
  }] as Filter[]), [manufacturers, t]);

  const isSelectedProgramExpired = !!selectedProgramPeriod &&
    selectedProgramPeriod.endDate < formatDateOnly(new Date(), 'America/Los_Angeles');

  let availablePointsTooltip: string;
  if (retailer?.isProgramCreationDisabled) {
    availablePointsTooltip = retailer.hasBookings
      ? t($ => $.overview.kpis.availablePointsPromoRetailerTooltip)
      : t($ => $.overview.kpis.availablePointsPromoRetailerNoBookingsTooltip);
  } else if (isSelectedProgramExpired) {
    availablePointsTooltip = retailer?.hasBookings
      ? t($ => $.overview.kpis.availablePointsTooltip)
      : t($ => $.overview.kpis.availablePointsNoBookingsTooltip);
  } else {
    availablePointsTooltip = retailer?.hasBookings
      ? t($ => $.overview.kpis.availablePointsAllTooltip)
      : t($ => $.overview.kpis.availablePointsAllNoBookingsTooltip);
  }

  const reservedPointsTooltip = isSelectedProgramExpired
    ? t($ => $.overview.kpis.reservedTooltip)
    : t($ => $.overview.kpis.reservedAllTooltip);

  const KPICardText = {
    Label: t($ => $.overview.kpis.availablePoints),
    Sublabel: retailer?.isProgramCreationDisabled
      ? undefined
      : isSelectedProgramExpired
        ? t($ => $.overview.kpis.selectedPeriod)
        : t($ => $.overview.kpis.allPeriods),
    TooltipText: availablePointsTooltip,
  };

  const retailerRedemptionRate = (kpis?.programPeriod.retailer.pointsRedemptionRate ?? 0) * 100;
  const manufacturerRedemptionRate = (
    kpis?.programPeriod.manufacturer.pointsRedemptionRate ?? 0
  ) * 100;

  const KPISection = (
    <Stack width="100%">
      <Stack direction="row" justifyContent="space-between">
        <Text category="h2">
          {t($ => $.overview.pointKPIs)}
        </Text>
        <Button
          onClick={() => (
            navigate(
              {
                pathname: Routes.REPORT_PAGE.replace(':reportType', ReportType.CustomerPoints),
                search: retailerSearch,
              },
              { state: { fromOverview: true, selectedProgram: selectedProgramPeriod } },
            )
          )}
          startIcon={<TableChartOutlined />}
          testID="points-kpis-details-button"
        >
          {t($ => $.common.actions.details)}
        </Button>
      </Stack>
      {!!retailer && !retailer.isProgramCreationDisabled && (
        <>
          <Text category="body-xlarge">
            {t($ => $.overview.retailer)}
          </Text>
          <VSpacer size="3" />
          <Grid container spacing={3}>
            <Grid item sx={{ display: 'flex' }} xs={retailer.hasBookings ? 3 : 4}>
              <KPICard
                label={KPICardText.Label}
                sublabel={KPICardText.Sublabel}
                testID="kpi-card-retailer-issued"
                tooltipText={KPICardText.TooltipText}
                valueText={kpis && formatNumber(
                  kpis.outstanding.retailer.pointsAvailable,
                  i18n,
                )}
              />
            </Grid>
            {retailer.hasBookings && (
              <Grid item sx={{ display: 'flex' }} xs={3}>
                <KPICard
                  label={t($ => $.overview.kpis.reservedPoints)}
                  sublabel={KPICardText.Sublabel}
                  testID="kpi-card-retailer-reserved"
                  tooltipText={reservedPointsTooltip}
                  valueText={kpis && formatNumber(kpis.outstanding.retailer.pointsReserved, i18n)}
                />
              </Grid>
            )}
            <Grid item sx={{ display: 'flex' }} xs={retailer.hasBookings ? 3 : 4}>
              <KPICard
                label={t($ => $.overview.kpis.redeemedPoints)}
                sublabel={
                  hasMultipleRewardPrograms
                    ? t($ => $.overview.kpis.selectedPeriod)
                    : undefined
                }
                testID="kpi-card-retailer-redeemed"
                tooltipText={t($ => $.overview.kpis.redeemedTooltip)}
                valueText={kpis && formatNumber(kpis.programPeriod.retailer.pointsRedeemed, i18n)}
              />
            </Grid>
            <Grid item sx={{ display: 'flex' }} xs={retailer.hasBookings ? 3 : 4}>
              <KPICard
                label={t($ => $.overview.kpis.redemptionRate)}
                sublabel={
                  hasMultipleRewardPrograms
                    ? t($ => $.overview.kpis.selectedPeriod)
                    : undefined
                }
                testID="kpi-card-retailer-rate"
                tooltipText={t($ => $.overview.kpis.redemptionRateTooltip)}
                valueText={
                  kpis && formatPercent(retailerRedemptionRate, i18n, { minimumFractionDigits: 2 })
                }
              />
            </Grid>
          </Grid>
          <VSpacer size="7" />
        </>
      )}
      <Text category="body-xlarge">
        {t($ => $.overview.manufacturer)}
      </Text>
      <VSpacer size="4" />
      <Toolbar
        filterSelections={manufacturerFilterSelections}
        filters={manufacturerFilter}
        hideSearch
        onChange={({ selections }) => {
          setManufacturerFilterSelections(selections);
        }}
        retainSelectionsOnFilterChange
        testID="points-overview-filters"
      />
      <VSpacer size="5" />
      <Grid container spacing={3}>
        <Grid item sx={{ display: 'flex' }} xs={retailer?.hasBookings ? 3 : 4}>
          <KPICard
            label={KPICardText.Label}
            sublabel={KPICardText.Sublabel}
            testID="kpi-card-manufacturer-available"
            tooltipText={KPICardText.TooltipText}
            valueText={kpis && formatNumber(
              kpis.outstanding.manufacturer.pointsAvailable,
              i18n,
            )}
          />
        </Grid>
        {retailer?.hasBookings && (
          <Grid item sx={{ display: 'flex' }} xs={3}>
            <KPICard
              label={t($ => $.overview.kpis.reservedPoints)}
              sublabel={KPICardText.Sublabel}
              testID="kpi-card-manufacturer-reserved"
              tooltipText={retailer?.isProgramCreationDisabled
                ? t($ => $.overview.kpis.reservedPromoRetailerTooltip)
                : reservedPointsTooltip
              }
              valueText={kpis && formatNumber(kpis.outstanding.manufacturer.pointsReserved, i18n)}
            />
          </Grid>
        )}
        <Grid item sx={{ display: 'flex' }} xs={retailer?.hasBookings ? 3 : 4}>
          <KPICard
            label={t($ => $.overview.kpis.redeemedPoints)}
            sublabel={
              hasMultipleRewardPrograms
                ? t($ => $.overview.kpis.selectedPeriod)
                : undefined
            }
            testID="kpi-card-manufacturer-redeemed"
            tooltipText={retailer?.isProgramCreationDisabled
              ? t($ => $.overview.kpis.redeemedPromoRetailerTooltip)
              : t($ => $.overview.kpis.redeemedTooltip)
            }
            valueText={kpis && formatNumber(kpis.programPeriod.manufacturer.pointsRedeemed, i18n)}
          />
        </Grid>
        <Grid item sx={{ display: 'flex' }} xs={retailer?.hasBookings ? 3 : 4}>
          <KPICard
            label={t($ => $.overview.kpis.redemptionRate)}
            sublabel={
              retailer?.isProgramCreationDisabled
                ? undefined
                : hasMultipleRewardPrograms
                  ? t($ => $.overview.kpis.selectedPeriod)
                  : undefined
            }
            testID="kpi-card-manufacturer-rate"
            tooltipText={retailer?.isProgramCreationDisabled
              ? t($ => $.overview.kpis.redemptionRatePromoRetailerTooltip)
              : t($ => $.overview.kpis.redemptionRateTooltip)
            }
            valueText={
              kpis && formatPercent(manufacturerRedemptionRate, i18n, { minimumFractionDigits: 2 })
            }
          />
        </Grid>
      </Grid>
    </Stack>
  );
  /* eslint-enable max-len */

  if (isInternal && (!retailerIdParam || isRetailerMissing)) {
    return <Navigate replace to={Routes.REPORTS} />;
  }

  const page = (
    <Container maxWidth="lg">
      <Stack alignItems="center">
        <Stack maxWidth="1216px" width="100%">
          {isInternal && (
            <Stack alignItems="center" direction="row" justifyContent="space-between">
              <Stack alignItems="flex-start" marginLeft="-14px">
                <Button
                  onClick={() => navigate({ pathname: Routes.REPORTS, search: retailerSearch })}
                  startIcon={<ArrowBack />}
                  testID="overview-back-button"
                  variant="text"
                >
                  {t($ => $.reports.backToReports)}
                </Button>
              </Stack>
              {!!retailerLogoUrl && !!retailer && (
                <img
                  alt={t($ => $.app.navigation.retailerLogo, { name: retailer.name })}
                  data-testid="overview-retailer-logo"
                  height={32}
                  src={retailerLogoUrl}
                />
              )}
            </Stack>
          )}
          {Header}
          <VSpacer size="5" />
          <ProgramPeriodSelector
            labelTextSize="body-large"
            onSelectProgram={setSelectedProgramPeriod}
            programs={agronomyProgramsOnly(retailer?.rewardsPrograms)}
            selectedProgram={selectedProgramPeriod}
            selectedValueTextSize="body-large"
          />
          <VSpacer size="5" />
          <Divider />
          <VSpacer size="5" />
          {KPISection}
          <VSpacer size="9" />
          <Divider />
          <VSpacer size="5" />
          <Text category="h3">
            {t($ => $.overview.customerPointsActivity)}
          </Text>
          <VSpacer size="7" />
          {!!retailer && (
            <>
              <Stack direction="row">
                <PointsOverviewCard
                  fullWidth={retailer.isProgramCreationDisabled}
                  retailer={retailer}
                  selectedProgram={selectedProgramPeriod}
                />
                {!!selectedProgramPeriod && !retailer.isProgramCreationDisabled && (
                  <>
                    <HSpacer size="7" />
                    <TierProgressCard retailer={retailer} selectedProgram={selectedProgramPeriod} />
                  </>
                )}
              </Stack>
              {isRetailerAdmin && retailer.isChurnEnabled && !!selectedProgramPeriod && (
                <>
                  <VSpacer size="9" />
                  <Divider />
                  <VSpacer size="5" />
                  <Stack direction="row" justifyContent="space-between">
                    <Stack alignItems="center" direction="row">
                      <Text category="h3" sx={{ display: 'flex', alignItems: 'center' }}>
                        {t($ => $.overview.customerChurn)}
                      </Text>
                      <HSpacer size="4" />
                      <img
                        alt="Beta"
                        height="16"
                        src={beta}
                        style={{ marginTop: '3px' }}
                      />
                    </Stack>
                    <Stack alignItems="center" direction="row">
                      <Button
                        onClick={() => setShowAboutInsightsModal(true)}
                        size="medium"
                        startIcon={<Insights />}
                        sx={{ "letter-spacing": "0.1px" }}
                        testID="about-insight-button"
                        variant="text"
                      >
                        About AI Insights
                      </Button>
                      <HSpacer size="2" />
                      <AxiomLogo />
                    </Stack>
                  </Stack>
                  <VSpacer size="7" />
                  <CustomerChurnSection
                    program={selectedProgramPeriod}
                    retailerId={retailer.id}
                  />
                </>
              )}
            </>
          )}
        </Stack>
      </Stack>
      {showAboutInsightsModal && (
        <AboutInsightsModal
          onClose={() => setShowAboutInsightsModal(false)}
          open={true}
        />
      )}
    </Container>
  );

  return brandTheme ? <ThemeProvider theme={brandTheme}>{page}</ThemeProvider> : page;
};
